import liveServerStatus from '../liveServerStatus';
import generateTaskAssignmentEmailHTML from './templates/taskAssignmentTemplate';
import generateTaskCommentEmailHTML from './templates/taskCommentTemplate';
import generateWeeklyReminderEmailHTML from './templates/weeklyReminderTemplate';
import generateMonthlyScoreEmailHTML from './templates/monthlyScoreTemplate';
import generateReportReviewedEmailHTML from './templates/reportReviewedTemplate';
import generateGameTestingReminderEmailHTML from './templates/gameTestingReminderTemplate';
import generateDocumentUploadEmailHTML from './templates/documentUploadTemplate';
import generateTaskStatusUpdateEmailHTML from './templates/taskStatusUpdateTemplate';
import generateEmailMigrationHTML from './templates/emailMigrationTemplate';
import { generateLeaveRequestEmailHTML } from './templates/leaveRequestTemplate';
import { generateClaimRequestEmailHTML } from './templates/claimRequestTemplate';
import { generateRegistrationEmailHTML } from './templates/registrationTemplate';
import generateProfileUpdateEmailHTML from './templates/profileUpdateTemplate';
import generateEmployeeTerminationEmailHTML from './templates/employeeTerminationTemplate';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const splitEmailList = (value) =>
  String(value || '')
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const envFallbackRecipients = [
  process.env.REACT_APP_MAIL_TWO,
  process.env.REACT_APP_MAIL_THREE,
  process.env.REACT_APP_MAIL_FOUR,
]
  .flatMap(splitEmailList)
  .filter(Boolean);

const mergeEmailRecipients = (...values) => {
  const all = values.flatMap(splitEmailList).filter(Boolean);
  return Array.from(new Set(all)).join(';');
};

/**
 * Process email subject based on server environment
 * Adds "[TEST]" prefix to subjects when not in live environment
 * @param {string} subject - The original email subject
 * @returns {string} - The processed email subject
 */
const processEmailSubject = (subject) => {
  return liveServerStatus ? subject : `[TEST] ${subject}`;
};

/**
 * Send an email using backend HR API (Resend)
 * @param {Object} params - Email parameters including mailSubject, mailBody, mailOne (recipient), etc.
 * @param {string} templateId - Optional legacy template marker (kept for compatibility)
 * @returns {Promise} - The EmailJS send result
 */
export const sendEmail = async (params, templateId = 'legacy-template') => {
  try {
    // Process subject based on server environment
    const processedSubject = processEmailSubject(params.mailSubject);
    
    const dynamicMailTwo = params.mailTwo;
    const dynamicMailThree = params.mailThree;
    const dynamicMailFour = params.mailFour;

    const mergedExtras = mergeEmailRecipients(
      dynamicMailTwo,
      dynamicMailThree,
      dynamicMailFour,
      envFallbackRecipients.join(';')
    );
    const extraRecipients = splitEmailList(mergedExtras);

    const templateParams = {
      ...params,
      mailSubject: processedSubject,
      reply_to: params.reply_to || 'info@myelinh.com',
      mailTwo: extraRecipients[0] || '',
      mailThree: extraRecipients[1] || '',
      mailFour: extraRecipients.slice(2).join(';')
    };

    // Log email in development/test environment
    if (!liveServerStatus) {
      console.log('Sending test email:', {
        to: templateParams.mailOne,
        subject: processedSubject,
        // Include other useful info for debugging
        isTestEnvironment: true
      });
    }

    const response = await fetch(`${API_BASE}/api/hr/ppc/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-ppc-secret': process.env.REACT_APP_PPC_EMAIL_API_SECRET || '',
      },
      body: JSON.stringify({
        emailType: templateId || 'ppc-transactional',
        payload: {
          ...templateParams,
          templateId,
        },
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to send email via backend');
    }
    
    // Log successful email send
    console.log('✅ Email sent successfully:', {
      to: templateParams.mailOne,
      subject: processedSubject,
      templateId,
      timestamp: new Date().toISOString()
    });
    
    return { success: true, result };
  } catch (error) {
    console.error('Error sending email:', error);
    return { success: false, error };
  }
};

/**
 * Send a task assignment email to the assignee
 * @param {Object} taskData - Task details
 * @param {Object} assignerData - Information about the person assigning the task
 * @param {Object} assigneeData - Information about the person receiving the task
 * @returns {Promise} - The email send result
 */
export const sendTaskAssignmentEmail = async (taskData, assignerData, assigneeData) => {
  try {
    console.log('📧 Sending task assignment email to:', assigneeData.email);
    
    // Generate HTML email content
    const mailBody = generateTaskAssignmentEmailHTML(taskData, assignerData, assigneeData);
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `New Task Assignment: ${taskData.title}`,
      mailBody,
      mailOne: assigneeData.email,
      reply_to: assignerData.email || 'info@myelinh.com'
    };
    
    // Send the email
    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Task assignment email sent successfully to:', assigneeData.email, 'Task:', taskData.title);
    } else {
      console.error('❌ Failed to send task assignment email to:', assigneeData.email, 'Error:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending task assignment email:', error);
    return { success: false, error };
  }
};

/**
 * Send task assignment emails to multiple assignees
 * @param {Object} taskData - Task details
 * @param {Object} assignerData - Information about the person assigning the task
 * @param {Array} assigneesData - Array of assignee information objects
 * @returns {Promise} - Results of all email sends
 */
export const sendTaskAssignmentEmails = async (taskData, assignerData, assigneesData) => {
  try {
    // For multiple recipients, we can either:
    // 1. Send individual emails to each assignee (more personalized)
    // 2. Send one email with all recipients in the To field (less traffic)
    
    // Option 1: Individual emails (more personalized)
    const emailPromises = assigneesData.map(assignee => 
      sendTaskAssignmentEmail(taskData, assignerData, assignee)
    );
    
    const results = await Promise.all(emailPromises);
    return { success: results.every(r => r.success), results };
    
    // Option 2: One email to all recipients
    /*
    // Join all recipient emails with semicolon
    const allRecipients = assigneesData.map(a => a.email).join(';');
    
    // Generate HTML content using the first assignee's name for personalization
    // Or use a more generic greeting for multiple recipients
    const mailBody = generateTaskAssignmentEmailHTML(
      taskData, 
      assignerData,
      { name: 'Team Member' } // Generic greeting for multiple recipients
    );
    
    const emailParams = {
      mailSubject: `New Task Assignment: ${taskData.title}`,
      mailBody,
      mailOne: allRecipients,
      reply_to: assignerData.email || 'info@myelinh.com'
    };
    
    return await sendEmail(emailParams);
    */
  } catch (error) {
    console.error('Error sending task assignment emails:', error);
    return { success: false, error };
  }
};

/**
 * Send notification email about a new comment on a task
 * @param {Object} taskData - Task details
 * @param {Object} commenterData - Information about the person who made the comment
 * @param {string} commentText - The text content of the comment
 * @param {Array} recipientEmails - Array of recipient email addresses
 * @returns {Promise} - The email send result
 */
export const sendTaskCommentNotification = async (taskData, commenterData, commentText, recipientEmails) => {
  try {
    if (!recipientEmails || recipientEmails.length === 0) {
      return { success: false, error: 'No recipients specified' };
    }

    // Generate HTML email content using the imported template
    const mailBody = generateTaskCommentEmailHTML(taskData, commenterData, commentText);
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] New comment on task: ${taskData.title}`,
      mailBody,
      mailOne: recipientEmails[0],
      mailTwo: recipientEmails.length > 1 ? recipientEmails[1] : '',
      mailThree: recipientEmails.length > 2 ? recipientEmails[2] : '',
      reply_to: commenterData.email || 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending task comment notification:', error);
    return { success: false, error };
  }
};

/**
 * Send Weekly Performance Report Reminder emails to employees
 * @param {Array} employees - Array of employee objects with name and email
 * @param {Object} weekData - Information about the current week (startDate, endDate)
 * @returns {Promise} - Results of all email sends
 */
export const sendWeeklyReportReminders = async (employees, weekData) => {
  try {
    if (!employees || employees.length === 0) {
      return { success: false, error: 'No employees specified' };
    }

    // Send individual emails to each employee (personalized)
    const emailPromises = employees.map(employee => {
      // Generate HTML email content
      const mailBody = generateWeeklyReminderEmailHTML(employee, weekData);
      
      // Prepare email parameters
      const emailParams = {
        mailSubject: `[MyelinZ PPC] Weekly Performance Report Reminder: ${weekData.startDate} - ${weekData.endDate}`,
        mailBody,
        mailOne: employee.email,
        reply_to: 'info@myelinh.com'
      };
      
      // Send the email
      return sendEmail(emailParams);
    });
    
    const results = await Promise.all(emailPromises);
    return { 
      success: results.every(r => r.success), 
      results,
      sentCount: results.filter(r => r.success).length
    };
  } catch (error) {
    console.error('Error sending Weekly Performance Report Reminders:', error);
    return { success: false, error };
  }
};

/**
 * Send monthly score notification to an employee
 * @param {Object} employeeData - Employee information
 * @param {Object} scoreData - Monthly score information
 * @param {string} customMessage - Optional custom message from admin
 * @returns {Promise} - The email send result
 */
export const sendMonthlyScoreNotification = async (employeeData, scoreData, customMessage = '') => {
  try {
    // Generate HTML email content
    const mailBody = generateMonthlyScoreEmailHTML(employeeData, scoreData, customMessage);
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Monthly Performance Score: ${scoreData.month} ${scoreData.year}`,
      mailBody,
      mailOne: employeeData.email,
      reply_to: 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending monthly score notification:', error);
    return { success: false, error };
  }
};

/**
 * Send multiple monthly score emails
 * @param {Array} employees - Array of employee objects
 * @param {Object} scoreData - Score data (can be customized per employee in the map function)
 * @param {string} customMessage - Optional custom message
 * @returns {Promise} - Results of all email sends
 */
export const sendMonthlyScoreEmails = async (employees, scoreData, customMessage = '') => {
  try {
    if (!employees || employees.length === 0) {
      return { success: false, error: 'No employees specified' };
    }

    // Send individual emails to each employee
    const emailPromises = employees.map(employee => 
      sendMonthlyScoreNotification(employee, scoreData, customMessage)
    );
    
    const results = await Promise.all(emailPromises);
    return { 
      success: results.every(r => r.success), 
      results,
      sentCount: results.filter(r => r.success).length
    };
  } catch (error) {
    console.error('Error sending monthly score emails:', error);
    return { success: false, error };
  }
};

/**
 * Send notification when a weekly report has been reviewed
 * @param {Object} employeeData - Employee information
 * @param {Object} reviewData - Review information including score and feedback
 * @returns {Promise} - The email send result
 */
export const sendReportReviewedNotification = async (employeeData, reviewData) => {
  try {
    // Generate HTML email content
    const mailBody = generateReportReviewedEmailHTML(employeeData, reviewData);
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Weekly Progress Report Reviewed`,
      mailBody,
      mailOne: employeeData.email,
      reply_to: reviewData.reviewerEmail || 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending report reviewed notification:', error);
    return { success: false, error };
  }
};

/**
 * Send Game Testing Reminder emails to employees
 * @param {Array} employees - Array of employee objects with name and email
 * @returns {Promise} - Results of all email sends
 */
export const sendGameTestingReminders = async (employees) => {
  try {
    if (!employees || employees.length === 0) {
      return { success: false, error: 'No employees specified' };
    }

    // Send individual emails to each employee (personalized)
    const emailPromises = employees.map(employee => {
      // Generate HTML email content
      const mailBody = generateGameTestingReminderEmailHTML(employee);
      
      // Prepare email parameters
      const emailParams = {
        mailSubject: `[MyelinZ PPC] Game/App Testing Reminder`,
        mailBody,
        mailOne: employee.email,
        reply_to: 'info@myelinh.com'
      };
      
      // Send the email
      return sendEmail(emailParams);
    });
    
    const results = await Promise.all(emailPromises);
    return { 
      success: results.every(r => r.success), 
      results,
      sentCount: results.filter(r => r.success).length
    };
  } catch (error) {
    console.error('Error sending Game Testing Reminder emails:', error);
    return { success: false, error };
  }
};

/**
 * Send document upload notification to an employee
 * @param {Object} employeeData - Employee information including name and email
 * @param {Object} documentData - Information about the uploaded document
 * @returns {Promise} - The email send result
 */
export const sendDocumentUploadNotification = async (employeeData, documentData) => {
  try {
    if (!employeeData || !employeeData.email) {
      return { success: false, error: 'No employee email specified' };
    }

    if (!documentData) {
      return { success: false, error: 'No document data provided' };
    }

    // Generate HTML email content
    const mailBody = generateDocumentUploadEmailHTML(employeeData, documentData);
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] New Document Available: ${documentData.category}`,
      mailBody,
      mailOne: employeeData.email,
      reply_to: 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending document upload notification:', error);
    return { success: false, error };
  }
};

/**
 * Send notification about task status update
 * @param {Object} taskData - Task details
 * @param {Object} updaterData - Information about the person who updated the task
 * @param {String} previousStatus - Previous status
 * @param {String} newStatus - New status
 * @param {Array} recipientEmails - Array of recipient email addresses
 * @returns {Promise} - The email send result
 */
export const sendTaskStatusUpdateNotification = async (taskData, updaterData, previousStatus, newStatus, recipientEmails) => {
  try {
    if (!recipientEmails || recipientEmails.length === 0) {
      return { success: false, error: 'No recipients specified' };
    }

    // Generate HTML email content using the imported template
    const mailBody = generateTaskStatusUpdateEmailHTML(
      taskData, 
      updaterData, 
      'status-update',
      previousStatus,
      newStatus
    );
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Task Status Updated: ${taskData.title}`,
      mailBody,
      mailOne: recipientEmails[0],
      mailTwo: recipientEmails.length > 1 ? recipientEmails[1] : '',
      mailThree: recipientEmails.length > 2 ? recipientEmails[2] : '',
      reply_to: updaterData.email || 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending task status update notification:', error);
    return { success: false, error };
  }
};

/**
 * Send notification about task being archived
 * @param {Object} taskData - Task details
 * @param {Object} updaterData - Information about the person who archived the task
 * @param {Array} recipientEmails - Array of recipient email addresses
 * @returns {Promise} - The email send result
 */
export const sendTaskArchiveNotification = async (taskData, updaterData, recipientEmails) => {
  try {
    if (!recipientEmails || recipientEmails.length === 0) {
      return { success: false, error: 'No recipients specified' };
    }

    // Generate HTML email content using the imported template
    const mailBody = generateTaskStatusUpdateEmailHTML(
      taskData, 
      updaterData, 
      'archive'
    );
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Task Archived: ${taskData.title}`,
      mailBody,
      mailOne: recipientEmails[0],
      mailTwo: recipientEmails.length > 1 ? recipientEmails[1] : '',
      mailThree: recipientEmails.length > 2 ? recipientEmails[2] : '',
      reply_to: updaterData.email || 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending task archive notification:', error);
    return { success: false, error };
  }
};

/**
 * Send notification about task being deleted
 * @param {Object} taskData - Task details
 * @param {Object} updaterData - Information about the person who deleted the task
 * @param {Array} recipientEmails - Array of recipient email addresses
 * @returns {Promise} - The email send result
 */
export const sendTaskDeleteNotification = async (taskData, updaterData, recipientEmails) => {
  try {
    if (!recipientEmails || recipientEmails.length === 0) {
      return { success: false, error: 'No recipients specified' };
    }

    // Generate HTML email content using the imported template
    const mailBody = generateTaskStatusUpdateEmailHTML(
      taskData, 
      updaterData, 
      'delete'
    );
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Task Deleted: ${taskData.title}`,
      mailBody,
      mailOne: recipientEmails[0],
      mailTwo: recipientEmails.length > 1 ? recipientEmails[1] : '',
      mailThree: recipientEmails.length > 2 ? recipientEmails[2] : '',
      reply_to: updaterData.email || 'info@myelinh.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending task delete notification:', error);
    return { success: false, error };
  }
};

/**
 * Send email migration notification to an employee
 * @param {Object} employeeData - Employee information
 * @param {string} oldEmail - The old email address
 * @param {string} newEmail - The new email address
 * @returns {Promise} - The email send result
 */
export const sendEmailMigrationNotification = async (employeeData, oldEmail, newEmail) => {
  try {
    // Generate HTML email content
    const mailBody = generateEmailMigrationHTML(employeeData, oldEmail, newEmail);
    
    // Send to both old and new email addresses
    const recipients = `${newEmail};${oldEmail}`;
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ] Email Migration - Your New Company Email`,
      mailBody,
      mailOne: recipients,
      reply_to: 'info@myelinz.com'
    };
    
    // Send the email
    return await sendEmail(emailParams);
  } catch (error) {
    console.error('Error sending email migration notification:', error);
    return { success: false, error };
  }
};

/**
 * Send leave request notification email to managers
 * @param {Object} leaveData - Leave request data
 * @param {Object} userData - Employee data who submitted the request
 * @returns {Promise} - Email send result
 */
export const sendLeaveRequestNotification = async (leaveData, userData) => {
  try {
    console.log('📧 Sending leave request notification email to managers');
    console.log('Leave data:', leaveData);
    console.log('User data:', userData);
    
    const managerEmails = [
      process.env.REACT_APP_MAIL_TWO || 'zied.tayeb@myelinh.com',
      process.env.REACT_APP_MAIL_THREE || 'samaher.garbaya@myelinh.com',
      process.env.REACT_APP_MAIL_FOUR || 'info@myelinh.com'
    ].filter(Boolean);

    console.log('Manager emails:', managerEmails);

    const emailParams = {
      mailSubject: `New Leave Application from ${userData.name || userData.displayName}`,
      mailBody: generateLeaveRequestEmailHTML(leaveData, userData, 'Manager', 'pending'),
      mailOne: managerEmails[0],
      mailTwo: userData.email,
      mailThree: managerEmails[1],
      mailFour: managerEmails[2],
      from_name: 'MyelinZ HR System'
    };

    console.log('📧 Sending leave request email with params:', emailParams);
    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Leave request notification sent successfully to managers');
      console.log('✅ Employee copy sent to:', userData.email);
    } else {
      console.error('❌ Failed to send leave request notification:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending leave request notification:', error);
    return { success: false, error };
  }
};

/**
 * Send profile update notification email
 * @param {Object} userData - User data including name and email
 * @param {string} updateType - Type of update (personal_info, bank_details, emergency_contact, declaration, documents)
 * @param {Object} updatedFields - Fields that were updated
 * @returns {Promise} - The email send result
 */
export const sendProfileUpdateNotification = async (userData, updateType, updatedFields = {}) => {
  try {
    console.log('📧 Sending profile update notification email to:', userData.email, 'Type:', updateType);
    
    // Get the user's first name for personalization
    const firstName = userData.name ? userData.name.split(' ')[0] : 'there';
    
    // Generate HTML email content
    const mailBody = generateProfileUpdateEmailHTML(
      firstName,
      updateType,
      updatedFields,
      userData.email
    );
    
    // Define subject based on update type
    const subjectMap = {
      personal_info: 'Personal Information Updated',
      bank_details: 'Bank Details Updated', 
      emergency_contact: 'Emergency Contact Updated',
      declaration: 'Declaration Details Updated',
      documents: 'Profile Documents Updated'
    };
    
    const subject = subjectMap[updateType] || 'Profile Updated';
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: `[MyelinZ PPC] ${subject}`,
      mailBody,
      mailOne: userData.email,
      reply_to: 'hr@myelinz.com'
    };
    
    // Send the email
    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Profile update notification sent successfully to:', userData.email, 'Type:', updateType);
    } else {
      console.error('❌ Failed to send profile update notification to:', userData.email, 'Error:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending profile update notification:', error);
    return { success: false, error };
  }
};

/**
 * Send leave approval notification email
 * @param {Object} leaveData - Leave request data
 * @param {Object} userData - Employee data who submitted the request
 * @param {string} status - New status (Approved/Rejected)
 * @param {string} managerRemarks - Manager's remarks
 * @returns {Promise} - Email send result
 */
export const sendLeaveApprovalNotification = async (leaveData, userData, status, managerRemarks = '') => {
  try {
    console.log('📧 Sending leave approval notification email to:', userData.email);
    
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Leave Request ${status}`,
      mailBody: generateLeaveRequestEmailHTML(leaveData, userData, userData.name, status.toLowerCase()),
      mailOne: userData.email,
      reply_to: 'hr@myelinz.com'
    };

    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Leave approval notification sent successfully to:', userData.email, 'Status:', status);
    } else {
      console.error('❌ Failed to send leave approval notification to:', userData.email, 'Error:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending leave approval notification:', error);
    return { success: false, error };
  }
};

/**
 * Send claim approval notification email
 * @param {Object} claimData - Claim request data
 * @param {Object} userData - Employee data who submitted the request
 * @param {string} status - New status (Approved/Rejected)
 * @param {string} managerRemarks - Manager's remarks
 * @returns {Promise} - Email send result
 */
export const sendClaimApprovalNotification = async (claimData, userData, status, managerRemarks = '') => {
  try {
    console.log('📧 Sending claim approval notification email to:', userData.email);
    
    const emailParams = {
      mailSubject: `[MyelinZ PPC] Claim Request ${status}`,
      mailBody: generateClaimRequestEmailHTML(claimData, userData, userData.name, status.toLowerCase()),
      mailOne: userData.email,
      reply_to: 'hr@myelinz.com'
    };

    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Claim approval notification sent successfully to:', userData.email, 'Status:', status);
    } else {
      console.error('❌ Failed to send claim approval notification to:', userData.email, 'Error:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending claim approval notification:', error);
    return { success: false, error };
  }
};

export const sendClaimSubmissionNotification = async (claimData, userData) => {
  try {
    if (!userData?.email) {
      return { success: false, error: 'No employee email provided for claim submission notification' };
    }

    const employeeName = userData.name || userData.displayName || userData.firstName || 'Employee';
    const emailParams = {
      mailSubject: `[MyelinZ PPC] New Claim Request Submitted`,
      mailBody: generateClaimRequestEmailHTML(claimData, userData, employeeName, 'pending'),
      mailOne: userData.email,
      reply_to: 'hr@myelinz.com',
    };

    return await sendEmail(emailParams, 'claim-submission');
  } catch (error) {
    console.error('❌ Error sending claim submission notification:', error);
    return { success: false, error };
  }
};

export const sendSubscriptionSubmissionNotification = async (subscriptionData, userData) => {
  try {
    if (!userData?.email) {
      return { success: false, error: 'No employee email provided for subscription submission notification' };
    }

    const employeeName = userData.name || userData.displayName || 'Employee';
    const amount = Number(subscriptionData.amount || 0);
    const subject = `[MyelinZ PPC] New Subscription Added`;
    const mailBody = `
      <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1f2937;">
        <h2 style="margin-bottom: 12px;">Subscription Added</h2>
        <p>Hello,</p>
        <p>${employeeName} has added a new subscription record in PPC.</p>
        <ul>
          <li><strong>Purpose:</strong> ${subscriptionData.purpose || 'N/A'}</li>
          <li><strong>Vendor:</strong> ${subscriptionData.vendor || 'N/A'}</li>
          <li><strong>Amount:</strong> ${subscriptionData.currency || 'USD'} ${amount.toFixed(2)}</li>
          <li><strong>Expense Date:</strong> ${subscriptionData.expenseDate || 'N/A'}</li>
          <li><strong>Employee Email:</strong> ${userData.email}</li>
        </ul>
        ${subscriptionData.notes ? `<p><strong>Notes:</strong> ${subscriptionData.notes}</p>` : ''}
        <p>This is an automated notification from MyelinZ PPC.</p>
      </div>
    `;

    const emailParams = {
      mailSubject: subject,
      mailBody,
      mailOne: userData.email,
      reply_to: 'hr@myelinz.com',
    };

    return await sendEmail(emailParams, 'subscription-submission');
  } catch (error) {
    console.error('❌ Error sending subscription submission notification:', error);
    return { success: false, error };
  }
};

export const sendRecruitmentCandidateAssignedNotification = async (candidateData) => {
  try {
    if (!candidateData) {
      return { success: false, error: 'Candidate data missing for recruitment assignment notification' };
    }

    const mailBody = `
      <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1f2937;">
        <h2>New Candidate Assigned for Assessment</h2>
        <p>A new candidate has been added to the recruitment queue.</p>
        <ul>
          <li><strong>Name:</strong> ${candidateData.name || 'N/A'}</li>
          <li><strong>Email:</strong> ${candidateData.email || 'N/A'}</li>
          <li><strong>Position:</strong> ${candidateData.positionTitle || 'N/A'}</li>
          <li><strong>Assessor 1:</strong> ${candidateData.assessorOne || 'N/A'}</li>
          <li><strong>Assessor 2:</strong> ${candidateData.assessorTwo || 'N/A'}</li>
        </ul>
      </div>
    `;

    return await sendEmail(
      {
        mailSubject: `[MyelinZ PPC] New Candidate for Assessment - ${candidateData.positionTitle || 'Recruitment'}`,
        mailBody,
        mailOne: candidateData.assessorOne || '',
        mailTwo: candidateData.assessorTwo || '',
        reply_to: 'hr@myelinz.com',
      },
      'recruitment-candidate-assigned'
    );
  } catch (error) {
    console.error('❌ Error sending recruitment assignment notification:', error);
    return { success: false, error };
  }
};

export const sendRecruitmentAssessorReviewNotification = async (candidateData, assessorData, reviewData) => {
  try {
    if (!candidateData) {
      return { success: false, error: 'Candidate data missing for assessor review notification' };
    }
    const reviewerName = assessorData?.displayName || assessorData?.email || 'Assessor';
    const mailBody = `
      <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1f2937;">
        <h2>Assessor Review Submitted</h2>
        <p>${reviewerName} submitted a recruitment review.</p>
        <ul>
          <li><strong>Candidate:</strong> ${candidateData.name || 'N/A'} (${candidateData.email || 'N/A'})</li>
          <li><strong>Position:</strong> ${candidateData.positionTitle || 'N/A'}</li>
          <li><strong>Score:</strong> ${reviewData?.score ?? 'N/A'}</li>
          <li><strong>Review:</strong> ${reviewData?.review || 'N/A'}</li>
        </ul>
      </div>
    `;

    return await sendEmail(
      {
        mailSubject: `[MyelinZ PPC] Assessor Review Submitted - ${candidateData.name || 'Candidate'}`,
        mailBody,
        mailOne: assessorData?.email || '',
        mailTwo: candidateData.assessorOne || '',
        mailThree: candidateData.assessorTwo || '',
        reply_to: 'hr@myelinz.com',
      },
      'recruitment-assessor-review'
    );
  } catch (error) {
    console.error('❌ Error sending assessor review notification:', error);
    return { success: false, error };
  }
};

export const sendRecruitmentAssessorReassignedNotification = async (
  candidateData,
  previousAssessors = [],
  newAssessors = []
) => {
  try {
    const previous = previousAssessors.filter(Boolean).join(', ') || 'N/A';
    const next = newAssessors.filter(Boolean).join(', ') || 'N/A';
    const mailBody = `
      <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1f2937;">
        <h2>Candidate Assessor Assignment Updated</h2>
        <p>Recruitment assessor assignment has been updated by admin.</p>
        <ul>
          <li><strong>Candidate:</strong> ${candidateData?.name || 'N/A'} (${candidateData?.email || 'N/A'})</li>
          <li><strong>Position:</strong> ${candidateData?.positionTitle || 'N/A'}</li>
          <li><strong>Previous Assessors:</strong> ${previous}</li>
          <li><strong>New Assessors:</strong> ${next}</li>
        </ul>
      </div>
    `;

    return await sendEmail(
      {
        mailSubject: `[MyelinZ PPC] Assessor Reassignment - ${candidateData?.name || 'Candidate'}`,
        mailBody,
        mailOne: next,
        mailTwo: previous,
        reply_to: 'hr@myelinz.com',
      },
      'recruitment-assessor-reassigned'
    );
  } catch (error) {
    console.error('❌ Error sending assessor reassignment notification:', error);
    return { success: false, error };
  }
};

export const sendRecruitmentFinalReviewNotification = async (
  candidateData,
  finalReview,
  finalRemarks
) => {
  try {
    const mailBody = `
      <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #1f2937;">
        <h2>Recruitment Final Review Completed</h2>
        <p>Admin has finalized the recruitment review.</p>
        <ul>
          <li><strong>Candidate:</strong> ${candidateData?.name || 'N/A'} (${candidateData?.email || 'N/A'})</li>
          <li><strong>Position:</strong> ${candidateData?.positionTitle || 'N/A'}</li>
          <li><strong>Final Decision:</strong> ${finalReview || 'Undecided'}</li>
          <li><strong>Remarks:</strong> ${finalRemarks || 'N/A'}</li>
        </ul>
      </div>
    `;

    return await sendEmail(
      {
        mailSubject: `[MyelinZ PPC] Final Recruitment Decision - ${candidateData?.name || 'Candidate'}`,
        mailBody,
        mailOne: candidateData?.email || '',
        mailTwo: candidateData?.assessorOne || '',
        mailThree: candidateData?.assessorTwo || '',
        reply_to: 'hr@myelinz.com',
      },
      'recruitment-final-review'
    );
  } catch (error) {
    console.error('❌ Error sending final recruitment review notification:', error);
    return { success: false, error };
  }
};

/**
 * Send new employee registration notification email to admins
 * @param {Object} registrationData - Registration data from new employee
 * @returns {Promise} - Email send result
 */
export const sendRegistrationNotification = async (registrationData) => {
  try {
    console.log('📧 Sending new employee registration notification to admins');
    
    const managerEmails = [
      process.env.REACT_APP_MAIL_TWO || 'zied.tayeb@myelinh.com',
      process.env.REACT_APP_MAIL_THREE || 'samaher.garbaya@myelinh.com',
      process.env.REACT_APP_MAIL_FOUR || 'info@myelinh.com'
    ].filter(Boolean);

    console.log('Manager emails for registration notification:', managerEmails);

    // Generate HTML email content
    const mailBody = generateRegistrationEmailHTML(registrationData);

    const emailParams = {
      mailSubject: `[MyelinZ PPC] New Employee Registration: ${registrationData.name}`,
      mailBody,
      mailOne: managerEmails[0],
      mailTwo: managerEmails[1],
      mailThree: managerEmails[2],
      reply_to: 'hr@myelinz.com'
    };

    console.log('📧 Sending registration notification email with params:', emailParams);
    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Registration notification sent successfully to admins');
    } else {
      console.error('❌ Failed to send registration notification:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending registration notification:', error);
    return { success: false, error };
  }
};

/**
 * Send employee termination notification email to HR team
 * @param {Object} employeeData - Employee information
 * @param {string} terminationReason - Reason for termination
 * @param {string} terminatedBy - Person who terminated the employee
 * @returns {Promise} - Email send result
 */
export const sendEmployeeTerminationNotification = async (employeeData, terminationReason, terminatedBy) => {
  try {
    console.log('📧 Sending employee termination notification to HR team');
    console.log('Terminated employee:', employeeData.name, employeeData.email);
    
    const hrEmails = [
      process.env.REACT_APP_MAIL_TWO || 'zied.tayeb@myelinh.com',
      process.env.REACT_APP_MAIL_THREE || 'samaher.garbaya@myelinh.com',
      process.env.REACT_APP_MAIL_FOUR || 'info@myelinh.com'
    ].filter(Boolean);

    console.log('HR emails for termination notification:', hrEmails);

    // Format termination date
    const terminationDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Generate HTML email content with test environment check
    const mailBody = generateEmployeeTerminationEmailHTML(
      employeeData, 
      terminationReason, 
      terminatedBy, 
      terminationDate, 
      !liveServerStatus // Pass test environment flag
    );

    const emailParams = {
      mailSubject: `[MyelinZ PPC] Employee Termination: ${employeeData.name}`,
      mailBody,
      mailOne: hrEmails[0],
      mailTwo: hrEmails[1],
      mailThree: hrEmails[2],
      reply_to: 'hr@myelinz.com'
    };

    console.log('📧 Sending termination notification email with params:', emailParams);
    const result = await sendEmail(emailParams);
    
    if (result.success) {
      console.log('✅ Termination notification sent successfully to HR team');
    } else {
      console.error('❌ Failed to send termination notification:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error sending termination notification:', error);
    return { success: false, error };
  }
};

// Export a default object with all the functions
const emailService = {
  sendEmail,
  sendTaskAssignmentEmail,
  sendTaskAssignmentEmails,
  sendTaskCommentNotification,
  sendWeeklyReportReminders,
  sendMonthlyScoreNotification,
  sendMonthlyScoreEmails,
  sendReportReviewedNotification,
  sendGameTestingReminders,
  sendDocumentUploadNotification,
  sendTaskStatusUpdateNotification,
  sendTaskArchiveNotification,
  sendTaskDeleteNotification,
  sendEmailMigrationNotification,
  sendLeaveRequestNotification,
  sendProfileUpdateNotification,
  sendLeaveApprovalNotification,
  sendClaimApprovalNotification,
  sendClaimSubmissionNotification,
  sendSubscriptionSubmissionNotification,
  sendRecruitmentCandidateAssignedNotification,
  sendRecruitmentAssessorReviewNotification,
  sendRecruitmentAssessorReassignedNotification,
  sendRecruitmentFinalReviewNotification,
  sendEmployeeTerminationNotification
}; 

export default emailService; 